import {isDistinctCanvasImageId} from '../../../../shared/savedBrandAdCanvas';
type DeliveryImage={id:string;src:string;workflowRunId?:number|null;qaPassed?:boolean|null;qaReason?:string|null};
type RecoveryOptions={retainAlias?:(id:string)=>boolean;onAlias?:(from:string,to:string)=>void};
const localGeometry=new Set(['x','y','displayWidth','displayHeight']);
/** Canonical content may change after QA; the user's layout remains local. */
function refreshedContent<T extends DeliveryImage>(image:T,canonical:T):T{
 if(!canonical.src)return image; // A persisted reservation is not a delivered replacement.
 const content=Object.fromEntries(Object.entries(canonical).filter(([key,value])=>!localGeometry.has(key)&&value!==undefined&&!(key==='qaPassed'||key==='qaReason'?value===null:false)));
 if(typeof canonical.qaPassed==='boolean')content.qaReason=canonical.qaReason??undefined;
 else if(canonical.src!==image.src){
  content.qaPassed=undefined;content.qaReason=undefined;
 }
 return Object.entries(content).every(([key,value])=>(image as Record<string,unknown>)[key]===value)?image:{...image,...content};
}
/** Recover saved content and identities without replacing user layout or distinct copies. */
export function reconcileImagePositions<T extends DeliveryImage>(current:T[],restored:T[],isVirtual:(image:T)=>boolean,options:RecoveryOptions={}):T[]{
 const canonicalById=new Map(restored.map(image=>[image.id,image]));
 const currentById=new Map(current.map(image=>[image.id,image]));
 const reserved=new Set(current.filter(image=>canonicalById.has(image.id)).map(image=>image.id));
 const used=new Set<string>();
 const result:T[]=[];
 for(const image of current){
  // Only an unpersisted generated preview may alias an existing canonical
  // client in this exact run. URL equality alone never removes a user copy.
  if(!canonicalById.has(image.id)&&/^img-gen-\d+-/.test(image.id)&&Number.isSafeInteger(image.workflowRunId)&&Number(image.workflowRunId)>0&&!options.retainAlias?.(image.id)){
   const matches=restored.filter(row=>!!row.src&&row.src===image.src&&row.workflowRunId===image.workflowRunId&&!/^img-gen-/.test(row.id)&&!isDistinctCanvasImageId(row.id));
   if(matches.length===1&&currentById.get(matches[0].id)?.workflowRunId===matches[0].workflowRunId){options.onAlias?.(image.id,matches[0].id);continue;}
  }
  const canonical=canonicalById.get(image.id)??(!isDistinctCanvasImageId(image.id)?restored.find(row=>!!row.src&&!isDistinctCanvasImageId(row.id)&&row.src===image.src&&!used.has(row.id)&&!reserved.has(row.id)):undefined);
  if(canonical){
   if(used.has(canonical.id))continue;
   used.add(canonical.id);
   result.push(isVirtual(image)?canonical:refreshedContent(image,canonical));
  }else if(!isVirtual(image)){result.push(image);}
 }
 for(const image of restored)if(!used.has(image.id)){result.push(image);used.add(image.id);}
 return result.length===current.length&&result.every((image,index)=>image===current[index])?current:result;
}
